import React, { useEffect, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { UserSearchResultExternal } from '@askporter/client-grieg-lyric';
import { API } from '../../api';
import { UserAutocompleteProps, UserAutocomplete } from '.';

export interface ManagedStateMultipleUserAutocompleteProps
  extends Omit<Extract<UserAutocompleteProps, { isMultiple: true }>, 'value' | 'isMultiple'> {
  selectedUserUids: string[];
}

/**
 * A UserAutocomplete component with its own managed state
 * it fetches the selected users on page load, using selectedUserUids array
 */
export const ManagedStateMultipleUserAutocomplete: React.FC<ManagedStateMultipleUserAutocompleteProps> = ({
  selectedUserUids = [],
  ...props
}) => {
  const [selectedUsers, setSelectedUsers] = useState<UserSearchResultExternal[]>([]);

  const results = useQueries({
    queries: selectedUserUids.map((uid) => ({
      queryKey: ['get-user-on-page-load', { selectedUserUid: uid }],
      queryFn: () => API().get({ path: `users/${uid}` }) as Promise<UserSearchResultExternal>,
      // We only want to make the request on page load as the autocomplete won't have the users associated with the uids from the url
      enabled: Boolean(selectedUserUids.length !== selectedUsers.length),
    })),
  });

  useEffect(() => {
    results.forEach((result) => {
      if (result.data && !selectedUsers.find((user) => user.uid === result.data.uid)) {
        setSelectedUsers((prev) => [...prev, result.data]);
      }
    });
  }, [results, selectedUsers]);

  return (
    <UserAutocomplete
      {...props}
      isMultiple
      value={selectedUsers}
      onChange={(value) => {
        setSelectedUsers(value);
        props.onChange(value);
      }}
    />
  );
};
